import { morphSection } from '@theme/section-renderer';

/**
 * Campaign token capture.
 *
 * Reads `?token=` from the URL, stores it in a first-party functional cookie, and
 * sets the `vip_token` cart attribute that the discount Function reads to gate the
 * VIP tier. Showing campaign prices is handled by `campaign-price.js`; the token is
 * NOT trusted for enforcement — the Function re-checks it server-side at checkout.
 *
 * On the cart page it also forces a recompute: Shopify only recalculates automatic
 * (campaign) discounts on a cart mutation or at checkout, so a reloaded cart would
 * otherwise show a stale price.
 */

const COOKIE = 'vip_token';
const MAX_AGE = 60 * 60 * 24 * 30; // 30 days

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
};

const setCartAttribute = async (token) => {
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { vip_token: token } }),
    });
  } catch (error) {
    console.error('[vip] could not set the vip_token cart attribute', error);
  }
};

const cartHasToken = async (token) => {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart.attributes?.vip_token === token;
  } catch {
    return false;
  }
};

const onCartPage = () => {
  const cartUrl = (window.Theme?.routes?.cart_url || '/cart').replace(/\/$/, '');
  return window.location.pathname.replace(/\/$/, '') === cartUrl;
};

/**
 * Force the cart to recompute + re-render. Re-setting the attribute is a no-op cart
 * mutation that makes Shopify recalculate discounts; we then morph the cart section
 * with the freshly rendered HTML (same mechanism the theme uses on quantity changes).
 */
const recalcCart = async (token) => {
  const cart = document.querySelector('cart-items-component');
  const sectionId = cart?.getAttribute('data-section-id');
  if (!sectionId) return;
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { vip_token: token },
        sections: [sectionId],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    const html = data?.sections?.[sectionId];
    if (html) morphSection(sectionId, html);
  } catch (error) {
    console.error('[vip] cart recalc failed', error);
  }
};

const init = async () => {
  const url = new URL(window.location.href);
  const param = url.searchParams.get('token');

  if (param) {
    writeCookie(COOKIE, param);
    await setCartAttribute(param);
    // Strip the token from the visible URL so it isn't shared or bookmarked.
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } else {
    // Returning link-holder: re-assert the attribute if the cart lost it (a new or
    // emptied cart drops attributes).
    const stored = readCookie(COOKIE);
    if (stored && !(await cartHasToken(stored))) {
      await setCartAttribute(stored);
    }
  }

  const token = param || readCookie(COOKIE);
  if (token && onCartPage()) await recalcCart(token);
};

init();
